/**
 * useMessagesSync Hook (Story 2.3.6.5)
 *
 * Global WebSocket subscription for messaging events.
 * Subscribes to user.{userGuid}.messages topic at app level so the
 * header badge updates on ALL pages, not just MessagesPage.
 *
 * Responsibilities:
 * - Subscribe to messages topic
 * - On MESSAGE_RECEIVED: invalidate unread total, show notification + sound
 *   when user is NOT viewing that conversation
 * - Coordinate with ChatArea via activeConversationRef to avoid duplicates
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  useWebSocketOptional,
  useRealtimeEventOptional,
  EventType,
  RealtimeTopics,
  type MessageReceivedPayload,
} from '@teletienda/ws-client/react';
import { useAuth } from '@teletienda/shared-auth-ui';
import { conversationKeys } from '@/hooks/useConversations';

/**
 * Module-level ref for the currently active (open) conversation ID.
 * ChatArea sets this on mount/unmount so the global handler can
 * suppress notifications for the conversation the user is viewing.
 */
export let activeConversationRef: string | null = null;

export function setActiveConversation(id: string | null): void {
  activeConversationRef = id;
}

/** Notification sound singleton */
let audioInstance: HTMLAudioElement | null = null;

function playNotificationSound(): void {
  try {
    if (!audioInstance) {
      audioInstance = new Audio('/sounds/message.wav');
    }
    audioInstance.currentTime = 0;
    audioInstance.play().catch(() => {
      /* browser may block autoplay */
    });
  } catch {
    /* ignore */
  }
}

export function useMessagesSync(): void {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const { isConnected, subscribe, unsubscribe } = useWebSocketOptional();
  // Subscribe to user's messages topic
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !user?.userGuid) return;

    const topic = RealtimeTopics.userMessages(user.userGuid);
    subscribe([topic]);

    return () => {
      unsubscribe([topic]);
    };
  }, [isConnected, isAuthenticated, user?.userGuid, subscribe, unsubscribe]);

  // Handle MESSAGE_RECEIVED events globally
  useRealtimeEventOptional<MessageReceivedPayload>(
    EventType.MESSAGE_RECEIVED,
    useCallback(
      (payload) => {
        // Skip own messages
        if (payload.senderGuid === user?.userGuid) return;

        const isActiveConversation =
          activeConversationRef === payload.conversationGuid;

        // Only invalidate for non-active conversations
        // ChatArea owns invalidation for the active conversation (messages + markAsRead)
        if (!isActiveConversation) {
          queryClient.invalidateQueries({
            queryKey: conversationKeys.unreadTotal(),
          });
          queryClient.invalidateQueries({
            queryKey: conversationKeys.lists(),
          });
          // Invalidate messages cache so opening this conversation later triggers a refetch
          // (useMessages has staleTime: Infinity — without this, cached data would be stale)
          queryClient.invalidateQueries({
            queryKey: conversationKeys.messages(payload.conversationGuid),
          });
          // Invalidate detail cache so ChatArea's useEffect sees fresh hasUnreadMessages: true
          // and triggers markAsRead (detail has staleTime: 60s — without this, stale cache
          // with hasUnreadMessages: false would prevent the markAsRead call)
          queryClient.invalidateQueries({
            queryKey: conversationKeys.detail(payload.conversationGuid),
          });
        }

        // Show notification only when NOT on the active conversation
        if (!isActiveConversation) {
          notifications.show({
            title: payload.senderName,
            message: payload.preview,
            color: 'blue',
            autoClose: 5000,
          });
        }

        // Play sound when tab hidden or not viewing that conversation
        if (document.hidden || !isActiveConversation) {
          playNotificationSound();
        }
      },
      [user?.userGuid, queryClient]
    ),
    [user?.userGuid]
  );
}
